import React from "react";
import { Text } from "evergreen-ui";
import { ColorPalette } from "../context/Theme";
import { AutocompleteMultiSelector } from "./AutocompleteMultiSelector";

/** A selectable product in the autocomplete / selection chips. */
export interface ProductOption {
    /** Shopify product id (string form). */
    id: string;
    title: string;
    /** Owning store name, shown next to the title to disambiguate. Null if unknown. */
    storeName: string | null;
    /** Linked ProductListing id, if any — makes the chip a link to product details. */
    productListingId: string | null;
}

export interface ProductAutocompleteMultiSelectorProps {
    /**
     * Runs the typeahead query. Caller supplies the data layer (already trims).
     * `storeId` is forwarded so the data layer can scope the query to a single
     * store; it is undefined for the unscoped (all-stores) case.
     */
    searchProducts: (titleContains: string, storeId?: string) => Promise<ProductOption[]>;
    /**
     * When set, results are scoped to this store and the store name is hidden in
     * the dropdown and chips (redundant when every product is from one store).
     * When undefined, all products are searched and the store name is shown to
     * disambiguate same-titled products across stores.
     */
    storeId?: string;
    /**
     * Called when `storeId` changes so the owner can clear its selection —
     * selections from a previous store scope should not carry over.
     */
    onStoreScopeChange?: () => void;
    /** Currently selected products, rendered as removable chips. */
    selectedProducts: ProductOption[];
    /** Called when the user picks a not-yet-selected result. */
    onAddProduct: (product: ProductOption) => void;
    /** Called when the user removes a chip. */
    onRemoveProduct: (productId: string) => void;
    placeholder?: string;
    maxWidth?: number;
    debounceMs?: number;
}

/**
 * Label for a product in the dropdown and chips: "<bold title> – <store name>".
 * Store name is omitted when unknown or hidden. Rendered as a fragment so
 * callers control the surrounding Text color/size.
 */
const productLabel = (product: ProductOption, showStoreName: boolean): JSX.Element => (
    <>
        <strong>{product.title || `(untitled) ${product.id}`}</strong>
        {showStoreName && product.storeName ? ` – ${product.storeName}` : ""}
    </>
);

/**
 * Chip label. When the product is linked to a ProductListing, the text is a
 * link that opens the product details page in a new tab; otherwise it renders
 * as plain (green) text.
 */
const ProductChipLabel: React.FC<{ product: ProductOption; showStoreName: boolean }> = ({ product, showStoreName }) => {
    if (!product.productListingId) {
        return (
            <Text fontSize={12} color={ColorPalette.primaryAccent} marginRight={4}>
                {productLabel(product, showStoreName)}
            </Text>
        );
    }
    return (
        <a
            href={`/products/${product.productListingId}/details`}
            target={"_blank"}
            rel={"noopener noreferrer"}
            title={"Open product details in a new tab"}
            style={{
                fontSize: 12,
                color: ColorPalette.primaryAccent,
                textDecoration: "none",
                marginRight: 4,
                cursor: "pointer",
            }}
        >
            {productLabel(product, showStoreName)}
        </a>
    );
};

/**
 * Product-specific typeahead multi-selector — a thin wrapper over the generic
 * {@link AutocompleteMultiSelector} that supplies product rendering (title +
 * store name, chip deep-links to product details), the store-scoping semantics,
 * and product copy. When `storeId` is set the store name is hidden and results
 * are scoped to that store.
 */
export const ProductAutocompleteMultiSelector: React.FC<ProductAutocompleteMultiSelectorProps> = ({
    searchProducts,
    storeId,
    onStoreScopeChange,
    selectedProducts,
    onAddProduct,
    onRemoveProduct,
    placeholder = "Type a product title to search...",
    maxWidth,
    debounceMs,
}) => (
    <AutocompleteMultiSelector<ProductOption>
        search={searchProducts}
        getId={(product) => product.id}
        renderOption={(product, { scoped }) => productLabel(product, !scoped)}
        renderChip={(product, { scoped }) => <ProductChipLabel product={product} showStoreName={!scoped} />}
        getRemoveLabel={(product) => `Remove ${product.title}`}
        selected={selectedProducts}
        onAdd={onAddProduct}
        onRemove={onRemoveProduct}
        scopeId={storeId}
        onScopeChange={onStoreScopeChange}
        placeholder={placeholder}
        emptyText={(query) => `No products match “${query}”.`}
        allSelectedText={"All matching products are already selected."}
        errorText={"Couldn't search products. Please try again."}
        maxWidth={maxWidth}
        debounceMs={debounceMs}
        searchInputTestId={"product-search-input"}
        selectedItemsTestId={"selected-products"}
    />
);
